import React, { useEffect, useState, useMemo, useCallback } from "react";
import { FaCheck } from "react-icons/fa6";
import { MapSectionProps } from "../../types/types";
import LazyMapImage from "../../components/LazyMapImage";
import { fetchDrivingRoute } from "../../services/amapService";
import {useTranslation} from "react-i18next"; // Import the service

/**
 * Truncates a polyline string by dropping every other point until
 * the total number of points is less than the specified maximum.
 *
 * @param polyline - The polyline string in format 'x1,y1;x2,y2;...'
 * @param maxPoints - Maximum number of points allowed (default: 1000)
 * @returns Truncated polyline string
 */
const truncatePolyline = (polyline: string, maxPoints: number = 2000): string => {
  if (!polyline || polyline.trim() === '') {
    return '';
  }

  // Split the polyline into individual points
  let points = polyline.split(';').filter(point => point.trim() !== '');

  // If already below the threshold, return as is
  if (points.length <= maxPoints) {
    return polyline;
  }

  // Keep dropping every other point until we're below the threshold
  while (points.length > maxPoints) {
    points = points.filter((_, index) => index % 2 === 0);
  }

  // Join the points back into a polyline string
  return points.join(';');
};

const VehicleOption: React.FC<{
  rideOption: string;
  onRideOptionChange: (option: string) => void;
}> = ({ rideOption, onRideOptionChange }) => {
  const { t } = useTranslation();

  const RideOption: React.FC<{ label: string; imgsrc: string }> = ({ label, imgsrc }) => (
    <label className="flex justify-between items-center cursor-pointer">
      <div className="flex items-center">
        <img src={imgsrc} alt={label} className="w-16 h-12 rounded-full" />
        <span className="text-gray-800">{label}</span>
      </div>
      <div className="flex items-center">
        <input
          type="radio"
          name="ride"
          value={label}
          className="peer sr-only"
          onChange={() => onRideOptionChange(label)}
          checked={rideOption === label}
        />
        <span className="w-5 h-5 rounded border border-gray-400 flex items-center justify-center peer-checked:bg-blue-500 peer-checked:border-blue-500">
          <FaCheck />
        </span>
      </div>
    </label>
  );

  return (
    <div
      className="bg-white p-4 rounded-l-lg shadow-lg flex flex-col justify-between"
      style={{ width: "30%" }}
    >
      <div>
        <h2 className="text-lg font-bold mb-2">{t('BookingSelectMode')}</h2>
        <p className="text-gray-600 mb-4 text-bold">{t('BookingChooseRide')}</p>
        <div className="space-y-2">
          <RideOption label={t('BookingComfortableCar')} imgsrc="/image/A_car.png" />
          <RideOption label={t('BookingBusinessCar')} imgsrc="/image/B_car.png" />
          <RideOption label={t('BookingLuxuryCar')} imgsrc="/image/C_car.png" />
        </div>
      </div>
    </div>
  );
};

const MapSection: React.FC<MapSectionProps> = ({
                                                 apiKey,
                                                 midPointLoc,
                                                 routeData,
                                                 rideOption,
                                                 onRideOptionChange,
                                               }) => {
  const { t } = useTranslation();
  const [polyline, setPolyline] = useState("");
  const [loadingRoute, setLoadingRoute] = useState(false);

  useEffect(() => {
    setPolyline("");
  }, [routeData]);

  useEffect(() => {
    if (!routeData || !routeData.location?.location || !routeData.destination?.location) return;

    const fetchRoutePlan = async () => {
      setLoadingRoute(true);
      try {
        const mergedPolyline = await fetchDrivingRoute({
          origin: routeData.location.location,
          destination: routeData.destination.location,
          apiKey,
        });

        // Truncate the polyline if it contains too many points
        const truncatedPolyline = truncatePolyline(mergedPolyline);

        setPolyline(truncatedPolyline);
      } catch (error) {
        console.error("Error fetching driving route data:", error);
        setPolyline(""); // Reset polyline on error
      } finally {
        setLoadingRoute(false);
      }
    };

    fetchRoutePlan();
  }, [routeData, apiKey]);

  const mapUrl = useMemo(() => {
    if (routeData) {
      if (!polyline) {
        return `https://restapi.amap.com/v3/staticmap?location=${midPointLoc}&zoom=8&size=950*580&markers=mid,0x008000,A:${routeData.location.location}|mid,0xffa500,B:${routeData.destination.location}&key=${apiKey}`;
      }
      return `https://restapi.amap.com/v3/staticmap?location=${midPointLoc}&zoom=8&size=950*580&markers=mid,0x008000,A:${routeData.location.location}|mid,0xffa500,B:${routeData.destination.location}&paths=5,0x0000FF,1,,:${polyline}&key=${apiKey}`;
    }
    return `https://restapi.amap.com/v3/staticmap?location=${midPointLoc}&zoom=8&size=950*580&key=${apiKey}`;
  }, [routeData, polyline, midPointLoc, apiKey]);

  if (!routeData) {
    return (
      <div className="col-span-3 flex justify-center w-full">
        <LazyMapImage
          main={true}
          src={mapUrl}
          alt="Location map"
          className="w-full h-auto rounded-lg shadow-lg"
        />
      </div>
    );
  }

  return (
    <div className="col-span-3 flex">
      {/*<VehicleOption */}
      {/*  rideOption={rideOption} */}
      {/*  onRideOptionChange={onRideOptionChange} */}
      {/*/>*/}
      <div className="relative" style={{ width: "100%" }}>
        <div className="relative w-full h-full">
          <LazyMapImage
            main={true}   // This prop is to separate the main map and the map with route, not used since the vehicle option is removed.
            src={mapUrl}
            alt="Location map"
            className="w-full h-full object-cover rounded-lg shadow-lg"
          />
          {loadingRoute && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-200 bg-opacity-70 rounded-r-lg">
              <div className="flex items-center space-x-3">
                <svg
                  className="w-5 h-5 animate-spin text-gray-600"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span className="text-gray-800 font-semibold">{t('BookingLoadingMap')}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(MapSection);